/**
 * @file SiteClosureHistory.tsx
 * @description История пауз сайта в разделе «Доступ к сайту» (phase19.16,
 * SB138; uiux-site-closed.md §2): «12.10 14:20–15:05 Иван · вручную / по
 * сроку / платформой», новые сверху, подгрузка по `nextCursor`. Список
 * перечитывается с начала, когда сменилось состояние паузы (`refreshKey`):
 * после своих действий и после опроса — строки руками не правим.
 * Перечитывание с начала отменяет начатую подгрузку (номер списка), строки
 * подгрузки без повторов по id; ошибка подгрузки — текстом под списком.
 */

import type { SiteClosureHistoryItemDto } from "@chaiberi/shared";
import { useCallback, useEffect, useRef, useState } from "react";

import { Button } from "../ui/button";
import { Skeleton } from "../ui/skeleton";
import { siteAccess } from "../../lib/api-site-access";
import { siteClosureHistoryRow } from "../../lib/site-access/site-access-view";

interface Props {
  /** Пояс магазина для дат. */
  zone: string;
  /** Меняется вместе с паузой — список перечитывается. */
  refreshKey: string;
}

interface HistoryState {
  items: SiteClosureHistoryItemDto[];
  nextCursor: string | null;
  status: "loading" | "ready" | "error";
  more: boolean;
  /** Не удалось подгрузить следующую страницу (уже показанные строки остаются). */
  moreError: boolean;
}

const EMPTY: HistoryState = { items: [], nextCursor: null, status: "loading", more: false, moreError: false };

/** Строки без повторов по id: страница могла сдвинуться, пока подгружали. */
function mergeUnique(prev: SiteClosureHistoryItemDto[], next: SiteClosureHistoryItemDto[]): SiteClosureHistoryItemDto[] {
  const seen = new Set(prev.map((item) => item.id));
  return [...prev, ...next.filter((item) => !seen.has(item.id))];
}

export function SiteClosureHistory({ zone, refreshKey }: Props) {
  const [s, setS] = useState<HistoryState>(EMPTY);
  // Номер списка: перечитали с начала — ответы прежних запросов (и подгрузки) не нужны.
  const listRef = useRef(0);

  const load = useCallback((cursor: string | null) => {
    if (!cursor) listRef.current += 1;
    const list = listRef.current;
    setS((prev) => (cursor ? { ...prev, more: true, moreError: false } : { ...prev, status: "loading", more: false, moreError: false }));
    siteAccess
      .history(cursor)
      .then((page) => {
        if (list !== listRef.current) return;
        setS((prev) => ({
          items: cursor ? mergeUnique(prev.items, page.items) : page.items,
          nextCursor: page.nextCursor,
          status: "ready",
          more: false,
          moreError: false,
        }));
      })
      .catch(() => {
        if (list !== listRef.current) return;
        setS((prev) => (cursor ? { ...prev, more: false, moreError: true } : { ...prev, status: "error", more: false }));
      });
  }, []);

  useEffect(() => {
    load(null);
    // Размонтирование — ответы уже ушедших запросов не пишутся.
    return () => {
      listRef.current += 1;
    };
  }, [load, refreshKey]);

  return (
    <section aria-labelledby="site-closure-history" className="flex flex-col gap-2">
      <h3 id="site-closure-history" className="text-sm font-medium text-on-surface">
        История
      </h3>
      {s.status === "loading" && s.items.length === 0 ? (
        <div className="flex flex-col gap-2" aria-busy="true">
          <Skeleton className="h-5 w-64 max-w-full" />
          <Skeleton className="h-5 w-56 max-w-full" />
        </div>
      ) : null}
      {s.status === "error" ? (
        <div className="flex flex-wrap items-center gap-2">
          <p className="type-body text-on-surface-variant">Не удалось загрузить историю.</p>
          <Button variant="outline" onClick={() => load(null)}>
            Повторить
          </Button>
        </div>
      ) : null}
      {s.status === "ready" && s.items.length === 0 ? <p className="type-body text-on-surface-variant">Сайт ещё не закрывали.</p> : null}
      {s.items.length > 0 ? (
        <ul className="flex flex-col divide-y divide-[var(--outline-variant)]" data-testid="site-closure-history">
          {s.items.map((item) => {
            const row = siteClosureHistoryRow(item, zone);
            return (
              <li key={row.id} className="type-body flex flex-wrap gap-x-2 py-2">
                <span className="tabular-nums text-on-surface">{row.period}</span>
                {/* Не обрезаем: на узком экране «кто · как» переносится второй строкой (QA D5). */}
                <span className="min-w-0 break-words text-on-surface-variant">
                  {row.who} · <span className={row.current ? "font-medium text-warning" : undefined}>{row.how}</span>
                </span>
              </li>
            );
          })}
        </ul>
      ) : null}
      {s.moreError ? <p className="type-caption text-destructive">Не удалось загрузить ещё — попробуйте снова.</p> : null}
      {s.nextCursor ? (
        <Button variant="outline" className="self-start" loading={s.more} onClick={() => load(s.nextCursor)}>
          Показать ещё
        </Button>
      ) : null}
    </section>
  );
}
